import React, { useState, useCallback, useRef } from 'react';
import { Play, Trash2, Plus, RotateCcw, Star, Sparkles, Volume2, Move, Lightbulb, Check, X, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

// ============ BLOCK DEFINITIONS ============
const BLOCK_TYPES = {
  // Event Blocks (Yellow)
  when_clicked: {
    type: 'event',
    color: '#F59E0B',
    label: 'When Clicked',
    icon: '👆',
    description: 'Start when something is clicked'
  },
  when_start: {
    type: 'event',
    color: '#F59E0B',
    label: 'When Start',
    icon: '▶️',
    description: 'Start when play is pressed'
  },
  
  // Action Blocks (Blue)
  glow: {
    type: 'action',
    color: '#3B82F6',
    label: 'Glow',
    icon: '✨',
    description: 'Make it glow brightly'
  },
  move_right: {
    type: 'action',
    color: '#3B82F6',
    label: 'Move Right',
    icon: '➡️',
    description: 'Move to the right'
  },
  move_up: {
    type: 'action',
    color: '#3B82F6',
    label: 'Move Up',
    icon: '⬆️',
    description: 'Move upward'
  },
  play_sound: {
    type: 'action',
    color: '#3B82F6',
    label: 'Play Sound',
    icon: '🔔',
    description: 'Play a magical sound'
  },
  spin: {
    type: 'action',
    color: '#3B82F6',
    label: 'Spin',
    icon: '🔄',
    description: 'Spin around'
  },
  grow: {
    type: 'action',
    color: '#3B82F6',
    label: 'Grow',
    icon: '📈',
    description: 'Get bigger'
  },
  
  // Logic Blocks (Green)
  repeat: {
    type: 'logic',
    color: '#22C55E',
    label: 'Repeat 3x',
    icon: '🔁',
    description: 'Do something 3 times',
    times: 3
  },
  wait: {
    type: 'logic',
    color: '#22C55E',
    label: 'Wait',
    icon: '⏱️',
    description: 'Wait a moment'
  },
  if_star: {
    type: 'logic',
    color: '#22C55E',
    label: 'If Star',
    icon: '⭐',
    description: 'Only if touching a star'
  }
};

// ============ CODING CHALLENGES ============
const CHALLENGES = [
  {
    id: 1,
    title: 'Make the Star Glow',
    description: 'When you click the star, make it glow!',
    hint: 'Start with "When Clicked" then add "Glow"',
    targetObject: 'star',
    solution: ['when_clicked', 'glow'],
    sparks: 5,
    // Educational explanation
    explanation: {
      whatItDoes: 'This code makes the star light up when someone clicks on it.',
      concept: 'Events and Actions - In coding, an EVENT is something that happens (like a click), and an ACTION is what the code does in response (like glowing).',
      whyItWorks: 'The "When Clicked" block waits for a click. When it detects one, it triggers the "Glow" action. This is how programs respond to what users do!'
    }
  },
  {
    id: 2,
    title: 'Dancing Star',
    description: 'Make the star spin when you press play!',
    hint: 'Start with "When Start" then add "Spin"',
    targetObject: 'star',
    solution: ['when_start', 'spin'],
    sparks: 5,
    explanation: {
      whatItDoes: 'This code makes the star spin around as soon as the program starts.',
      concept: 'Program Start Events - Many programs do things automatically when they begin, not just when you click. This is called a "start event".',
      whyItWorks: '"When Start" triggers immediately when you press play. The "Spin" action then makes the star rotate. No clicking needed!'
    }
  },
  {
    id: 3,
    title: 'Moving Star',
    description: 'Move the star to the right when clicked!',
    hint: 'Use "When Clicked" and "Move Right"',
    targetObject: 'star',
    solution: ['when_clicked', 'move_right'],
    sparks: 8,
    explanation: {
      whatItDoes: 'Each time you click, the star moves to the right side of the screen.',
      concept: 'Movement Commands - Coding can control where things appear on screen. "Move Right" changes the star\'s position.',
      whyItWorks: 'The click event triggers the move command. If you click multiple times, the star keeps moving right. This is how game characters move!'
    }
  },
  {
    id: 4,
    title: 'Musical Star',
    description: 'Make the star play a sound when clicked!',
    hint: 'Events start actions - what starts, what happens?',
    targetObject: 'star',
    solution: ['when_clicked', 'play_sound'],
    sparks: 8,
    explanation: {
      whatItDoes: 'Clicking the star plays a magical sound effect.',
      concept: 'Output - Code can produce different kinds of output: visuals (glow), movement, and sounds! This is how games and apps become interactive.',
      whyItWorks: 'Just like glow and move, "Play Sound" is another type of action. The same event (click) can trigger different actions depending on what you connect.'
    }
  },
  {
    id: 5,
    title: 'Triple Glow',
    description: 'Make the star glow 3 times in a row!',
    hint: 'Use the "Repeat" block to do something multiple times',
    targetObject: 'star',
    solution: ['when_start', 'repeat', 'glow'],
    sparks: 10,
    explanation: {
      whatItDoes: 'When the program starts, the star glows three times in a row, one after another.',
      concept: 'Loops - A LOOP repeats actions multiple times. Instead of writing "glow, glow, glow", we use "repeat 3x glow". This saves time and makes code cleaner!',
      whyItWorks: 'The "Repeat" block runs whatever comes after it multiple times. Loops are one of the most powerful ideas in programming - they let you do lots of work with just a few blocks!'
    }
  }
];

// ============ DRAGGABLE BLOCK COMPONENT ============
const DraggableBlock = ({ blockId, block, onDragStart, inPalette = true }) => {
  const handleDragStart = (e) => {
    e.dataTransfer.setData('blockId', blockId);
    e.dataTransfer.effectAllowed = 'copy';
    onDragStart && onDragStart(blockId);
  };
  
  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className={`flex items-center gap-2 px-4 py-3 rounded-xl cursor-grab active:cursor-grabbing transition-all hover:scale-105 ${
        inPalette ? 'opacity-90 hover:opacity-100' : ''
      }`}
      style={{ 
        backgroundColor: `${block.color}30`,
        border: `2px solid ${block.color}`,
        boxShadow: `0 2px 8px ${block.color}20`
      }}
      data-testid={`block-${blockId}`}
    >
      <span className="text-xl">{block.icon}</span>
      <span className="text-white font-medium">{block.label}</span>
    </div>
  );
};

// ============ CODING WORKSPACE ============
const CodingWorkspace = ({ blocks, onRemoveBlock, onDrop, onReorder }) => {
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    const blockId = e.dataTransfer.getData('blockId');
    if (blockId) {
      onDrop(blockId);
    }
  };
  
  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="min-h-[200px] bg-slate-900/50 border-2 border-dashed border-slate-600 rounded-xl p-4"
      data-testid="coding-workspace"
    >
      {blocks.length === 0 ? (
        <div className="h-full flex items-center justify-center text-slate-500">
          <div className="text-center">
            <Plus className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Drag blocks here to build your code!</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {blocks.map((blockId, index) => {
            const block = BLOCK_TYPES[blockId];
            if (!block) return null;
            
            return (
              <div key={index} className="flex items-center gap-2 group">
                <div className="flex-1">
                  <DraggableBlock blockId={blockId} block={block} inPalette={false} />
                </div>
                <button
                  onClick={() => onRemoveBlock(index)}
                  className="opacity-0 group-hover:opacity-100 p-2 text-red-400 hover:text-red-300 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
                {index < blocks.length - 1 && (
                  <ArrowRight className="absolute right-12 text-slate-600 w-4 h-4" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============ PREVIEW AREA ============
const PreviewArea = ({ targetObject, isRunning, effects }) => {
  const getObjectEmoji = () => {
    switch (targetObject) {
      case 'star': return '⭐';
      case 'moon': return '🌙';
      case 'planet': return '🪐';
      default: return '⭐';
    }
  };
  
  return (
    <div className="relative h-48 bg-gradient-to-b from-indigo-950 to-slate-900 rounded-xl overflow-hidden flex items-center justify-center">
      {/* Background stars */}
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-white/30 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`
          }}
        />
      ))}
      
      {/* Target object */}
      <div
        className={`text-6xl transition-all duration-300 ${
          effects.includes('glow') ? 'drop-shadow-[0_0_20px_rgba(251,191,36,0.8)]' : ''
        } ${
          effects.includes('spin') ? 'animate-spin' : ''
        } ${
          effects.includes('grow') ? 'scale-150' : ''
        }`}
        style={{
          transform: `
            translateX(${effects.includes('move_right') ? '40px' : '0'})
            translateY(${effects.includes('move_up') ? '-30px' : '0'})
          `
        }}
        data-testid="preview-object"
      >
        {getObjectEmoji()}
      </div>
      
      {/* Running indicator */}
      {isRunning && (
        <div className="absolute top-2 right-2 flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-green-400 text-sm">Running</span>
        </div>
      )}
    </div>
  );
};

// ============ MAIN VIBE CODING GARDEN ============
const VibeCodingGarden = ({ onComplete, onBack }) => {
  const [currentChallenge, setCurrentChallenge] = useState(CHALLENGES[0]);
  const [workspaceBlocks, setWorkspaceBlocks] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [activeEffects, setActiveEffects] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const audioContext = useRef(null);
  
  const playSound = useCallback(() => {
    if (!audioContext.current) {
      audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContext.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.frequency.value = 523.25; // C5
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  }, []);
  
  const handleAddBlock = (blockId) => {
    setWorkspaceBlocks(prev => [...prev, blockId]);
    setShowHint(false);
  };
  
  const handleRemoveBlock = (index) => {
    setWorkspaceBlocks(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleClear = () => {
    setWorkspaceBlocks([]);
    setActiveEffects([]);
    setIsRunning(false);
  };
  
  const handleRun = async () => {
    if (workspaceBlocks.length === 0) {
      toast.error('Add some blocks first!');
      return;
    }
    
    // Check if first block is an event
    const firstBlock = BLOCK_TYPES[workspaceBlocks[0]];
    if (!firstBlock || firstBlock.type !== 'event') {
      toast.error('Start with an event block (yellow)!');
      return;
    }
    
    setIsRunning(true);
    setActiveEffects([]);
    
    // Execute blocks sequentially
    let repeatCount = 1;
    for (let i = 1; i < workspaceBlocks.length; i++) {
      const blockId = workspaceBlocks[i];
      const block = BLOCK_TYPES[blockId];
      
      if (block.type === 'logic' && blockId === 'repeat') {
        repeatCount = 3;
        continue;
      }
      
      if (block.type === 'logic' && blockId === 'wait') {
        await new Promise(resolve => setTimeout(resolve, 500));
        continue;
      }
      
      // Execute action blocks
      for (let r = 0; r < repeatCount; r++) {
        if (blockId === 'play_sound') {
          playSound();
        }
        
        setActiveEffects(prev => [...prev, blockId]);
        await new Promise(resolve => setTimeout(resolve, 400));
        
        if (repeatCount > 1 && r < repeatCount - 1) {
          setActiveEffects([]);
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      repeatCount = 1;
    }
    
    // Check if solution matches
    setTimeout(() => {
      checkSolution();
    }, 500);
  };
  
  const checkSolution = () => {
    const isCorrect = currentChallenge.solution.every((blockId, index) => 
      workspaceBlocks[index] === blockId
    ) && workspaceBlocks.length === currentChallenge.solution.length;
    
    if (isCorrect) {
      toast.success(`Challenge Complete! +${currentChallenge.sparks} sparks! 🎉`);
      setCompletedChallenges(prev => [...prev, currentChallenge.id]);
      onComplete && onComplete(currentChallenge.sparks);
    } else {
      // Partial credit for trying
      const hasEventBlock = workspaceBlocks.length > 0 && BLOCK_TYPES[workspaceBlocks[0]]?.type === 'event';
      if (hasEventBlock) {
        toast.info('Good try! Check your blocks match the challenge.');
      }
    }
    
    setIsRunning(false);
  };
  
  const nextChallenge = () => {
    const currentIndex = CHALLENGES.findIndex(c => c.id === currentChallenge.id);
    if (currentIndex < CHALLENGES.length - 1) {
      setCurrentChallenge(CHALLENGES[currentIndex + 1]);
      handleClear();
    }
  };
  
  const blockCategories = {
    events: Object.entries(BLOCK_TYPES).filter(([_, b]) => b.type === 'event'),
    actions: Object.entries(BLOCK_TYPES).filter(([_, b]) => b.type === 'action'),
    logic: Object.entries(BLOCK_TYPES).filter(([_, b]) => b.type === 'logic')
  };
  
  return (
    <div className="glass-panel p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-400" />
          Vibe Coding Garden
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-purple-400 text-sm">
            Challenge {currentChallenge.id}/{CHALLENGES.length}
          </span>
          <span className="text-amber-400 text-sm">+{currentChallenge.sparks} sparks</span>
        </div>
      </div>
      
      {/* Challenge description */}
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
        <h3 className="text-lg font-semibold text-white mb-1">{currentChallenge.title}</h3>
        <p className="text-slate-300">{currentChallenge.description}</p>
        {completedChallenges.includes(currentChallenge.id) && (
          <div className="mt-2 flex items-center gap-2 text-green-400">
            <Check className="w-4 h-4" />
            <span className="text-sm">Completed!</span>
          </div>
        )}
      </div>
      
      {/* Learning Explanation Panel */}
      {currentChallenge.explanation && (
        <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-4 mb-6" data-testid="coding-explanation">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-indigo-300 mb-1">What You're Learning</h4>
              <p className="text-sm text-slate-300">{currentChallenge.explanation.concept}</p>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-indigo-500/20">
            <div className="bg-slate-900/30 rounded-lg p-3">
              <p className="text-xs text-cyan-400 font-medium mb-1">What the code does:</p>
              <p className="text-xs text-slate-400">{currentChallenge.explanation.whatItDoes}</p>
            </div>
            <div className="bg-slate-900/30 rounded-lg p-3">
              <p className="text-xs text-green-400 font-medium mb-1">Why it works:</p>
              <p className="text-xs text-slate-400">{currentChallenge.explanation.whyItWorks}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left side - Block palette and workspace */}
        <div className="space-y-4">
          {/* Block palette */}
          <div className="bg-slate-900/50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-slate-400 mb-3">Blocks</h4>
            
            <div className="space-y-3">
              <div>
                <p className="text-xs text-amber-400 mb-2">Events (Start here!)</p>
                <div className="flex flex-wrap gap-2">
                  {blockCategories.events.map(([id, block]) => (
                    <DraggableBlock key={id} blockId={id} block={block} onDragStart={handleAddBlock} />
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-xs text-blue-400 mb-2">Actions</p>
                <div className="flex flex-wrap gap-2">
                  {blockCategories.actions.map(([id, block]) => (
                    <DraggableBlock key={id} blockId={id} block={block} onDragStart={handleAddBlock} />
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-xs text-green-400 mb-2">Logic</p>
                <div className="flex flex-wrap gap-2">
                  {blockCategories.logic.map(([id, block]) => (
                    <DraggableBlock key={id} blockId={id} block={block} onDragStart={handleAddBlock} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Workspace */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-400">Your Code</h4>
              <button
                onClick={handleClear}
                className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                Clear
              </button>
            </div>
            <CodingWorkspace
              blocks={workspaceBlocks}
              onRemoveBlock={handleRemoveBlock}
              onDrop={handleAddBlock}
            />
          </div>
        </div>
        
        {/* Right side - Preview and controls */}
        <div className="space-y-4">
          <PreviewArea
            targetObject={currentChallenge.targetObject}
            isRunning={isRunning}
            effects={activeEffects}
          />
          
          {/* Controls */}
          <div className="flex gap-3">
            <button
              onClick={handleRun}
              disabled={isRunning}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="run-code-btn"
            >
              <Play className="w-5 h-5" />
              Run Code
            </button>
            
            {completedChallenges.includes(currentChallenge.id) && currentChallenge.id < CHALLENGES.length && (
              <button
                onClick={nextChallenge}
                className="px-6 py-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-xl transition-all"
              >
                Next Challenge
              </button>
            )}
          </div>
          
          {/* Hint */}
          {showHint ? (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-300 text-sm">{currentChallenge.hint}</p>
            </div>
          ) : (
            <button
              onClick={() => setShowHint(true)}
              className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors text-sm"
            >
              <Lightbulb className="w-4 h-4" />
              Need a hint?
            </button>
          )}
        </div>
      </div>
      
      {/* Back button */}
      <div className="mt-6 text-center">
        <button
          onClick={onBack}
          className="text-slate-400 hover:text-white transition-colors"
        >
          ← Back to Courtyard
        </button>
      </div>
    </div>
  );
};

export default VibeCodingGarden;
